import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PeopleIcon from "@mui/icons-material/People";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import ShareIcon from "@mui/icons-material/Share";
import { BASE_API_URL, BASE_SITE_URL } from "../../config";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type GalleryViewPopupProps = {
  captureId: number | null;
  onClose: () => void;
  userVote: string | null;
  onVote: (captureId: number, voteType: string) => void;
  isAuthenticated: boolean;
  currentUserId?: number | null;
  postCommands?: any;
};

const GalleryViewPopup = ({
  captureId,
  onClose,
  userVote,
  onVote,
  isAuthenticated,
  currentUserId,
  postCommands,
}: GalleryViewPopupProps) => {
  const { t } = useTranslation();
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [togglingPublish, setTogglingPublish] = useState(false);
  const [togglingTry, setTogglingTry] = useState(false);

  useEffect(() => {
    if (!captureId) return;

    setLoading(true);
    fetch(`${BASE_API_URL}/api/gallery/${captureId}`)
      .then((res) => res.json())
      .then((data) => {
        setItem(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [captureId]);

  if (!captureId) return null;

  const handleVote = (voteType: string) => {
    if (!item) return;
    // Optimistically update local counts
    const updated = { ...item };
    if (userVote === voteType) {
      // Removing vote
      const col = voteType === "like" ? "like_count" : "dislike_count";
      updated[col] = Math.max((updated[col] || 1) - 1, 0);
    } else if (userVote) {
      // Switching vote
      const addCol = voteType === "like" ? "like_count" : "dislike_count";
      const removeCol = voteType === "like" ? "dislike_count" : "like_count";
      updated[addCol] = (updated[addCol] || 0) + 1;
      updated[removeCol] = Math.max((updated[removeCol] || 1) - 1, 0);
    } else {
      // New vote
      const col = voteType === "like" ? "like_count" : "dislike_count";
      updated[col] = (updated[col] || 0) + 1;
    }
    setItem(updated);
    // Call parent handler (updates parent state + server)
    onVote(captureId, voteType);
  };

  const displayName = item?.alias_name || item?.user_name?.split(" ")[0] || t("gallery.anonymous");
  const inspiredByName = item?.inspired_by_alias || item?.inspired_by_name?.split(" ")[0];

  const getTypeLabel = () => {
    if (!item?.list_type) return null;
    if (item.group_by && item.group_by !== "All") return `${item.list_type} · ${item.group_by}`;
    return item.list_type;
  };

  const getTryUrl = () => {
    if (!item?.list_type) return null;
    const lt = item.list_type.toLowerCase();
    const gb = item.group_by && item.group_by !== "All" ? item.group_by : null;
    const basePath = gb
      ? `/tier-list/${lt}/${encodeURIComponent(gb)}`
      : `/tier-list/${lt}`;
    const params = new URLSearchParams();
    if (item.custom_tiers) {
      try {
        const tiers = typeof item.custom_tiers === "string" ? JSON.parse(item.custom_tiers) : item.custom_tiers;
        if (Array.isArray(tiers) && tiers.length > 0) {
          params.set("customTiers", btoa(JSON.stringify(tiers)));
        }
      } catch { /* ignore */ }
    }
    params.set("from", String(item.Id));
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  };

  const handleTryClick = () => {
    if (!item) return;
    fetch(`${BASE_API_URL}/api/gallery/try`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ captureId: item.Id }),
    }).catch(() => {});
  };

  // Parse custom tiers for display
  const getCustomTierNames = (): string[] | null => {
    if (!item?.custom_tiers) return null;
    try {
      const tiers = typeof item.custom_tiers === "string" ? JSON.parse(item.custom_tiers) : item.custom_tiers;
      if (Array.isArray(tiers) && tiers.length > 0) {
        return tiers.map((t: any) => t.name);
      }
    } catch { /* ignore */ }
    return null;
  };

  const customTierNames = item ? getCustomTierNames() : null;
  const isOwner = currentUserId && item?.user_id === currentUserId;

  const handleTogglePublish = async () => {
    if (!item || !postCommands) return;
    setTogglingPublish(true);
    try {
      await postCommands.unpublishFromGallery(item.Id);
      onClose();
    } catch (err) {
      console.error("Error toggling publish:", err);
    } finally {
      setTogglingPublish(false);
    }
  };

  const handleToggleTry = async () => {
    if (!item || !postCommands) return;
    setTogglingTry(true);
    try {
      const result = await postCommands.toggleAllowTry(item.Id);
      if (result) {
        setItem({ ...item, allow_try: result.allow_try });
      }
    } catch (err) {
      console.error("Error toggling public use:", err);
    } finally {
      setTogglingTry(false);
    }
  };

  return (
    <Dialog id="gallery-view-popup" open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6">{item?.title || item?.type || "Tier List"}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              {t("gallery.by")}{" "}
              <RouterLink
                to={`/profile/${item?.alias_name || item?.user_id}`}
                style={{ color: "inherit", textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                {displayName}
              </RouterLink>
            </Typography>
            {getTypeLabel() && (
              <Chip label={getTypeLabel()} size="small" sx={{ fontSize: "0.7rem", height: 20 }} />
            )}
          </Box>
          {inspiredByName && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {t("gallery.inspiredBy")}{" "}
              {item?.inspired_by_user_id ? (
                <RouterLink
                  to={`/profile/${item.inspired_by_alias || item.inspired_by_user_id}`}
                  style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  {inspiredByName}
                </RouterLink>
              ) : (
                inspiredByName
              )}
            </Typography>
          )}
          {customTierNames && (
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary">{t("gallery.tiers")}</Typography>
              {customTierNames.map((name, i) => (
                <Chip key={i} label={name} size="small" sx={{ fontSize: "0.6rem", height: 18 }} />
              ))}
            </Box>
          )}
        </Box>
        {item && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleVote("like")}
              color={userVote === "like" ? "primary" : "default"}
              disabled={!isAuthenticated}
            >
              {userVote === "like" ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            </IconButton>
            <Typography variant="body2">{item.like_count || 0}</Typography>
            <IconButton
              size="small"
              onClick={() => handleVote("dislike")}
              color={userVote === "dislike" ? "error" : "default"}
              disabled={!isAuthenticated}
            >
              {userVote === "dislike" ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
            </IconButton>
            <Typography variant="body2">{item.dislike_count || 0}</Typography>
            <VisibilityIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
            <Typography variant="body2">{item.view_count || 0}</Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center", p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : item?.screenshotInfo ? (
          <Box
            component="img"
            src={item.screenshotInfo}
            alt={item.title || "Tier List"}
            sx={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        ) : (
          <Typography color="text.secondary">{t("gallery.imageNotAvailable")}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isOwner && postCommands && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
            <Button
              onClick={handleTogglePublish}
              variant="outlined"
              size="small"
              disabled={togglingPublish}
              startIcon={
                togglingPublish ? (
                  <CircularProgress size={16} />
                ) : (
                  <PublicOffIcon />
                )
              }
              sx={{ textTransform: "none" }}
            >
              {t("community.unpublish")}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={item?.allow_try === 1}
                  onChange={handleToggleTry}
                  disabled={togglingTry}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--accent)" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--accent)" },
                  }}
                />
              }
              label={
                <Typography variant="caption">{t("capture.allowTry")}</Typography>
              }
            />
          </Box>
        )}
        {!isOwner && getTryUrl() && item?.allow_try === 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
            <Button
              component="a"
              href={getTryUrl()!}
              onClick={handleTryClick}
              startIcon={<PlayArrowIcon />}
              color="primary"
              variant="outlined"
              size="small"
            >
              {t("gallery.tryTierList")}
            </Button>
            {item?.try_count > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PeopleIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                <Typography variant="caption" color="text.secondary">
                  {item.try_count}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Button
          onClick={() => {
            const url = `${BASE_SITE_URL}/share/${captureId}`;
            navigator.clipboard.writeText(url).then(() => {
              toast.success(t("toast.linkCopied") || "Link copied!");
            }).catch(() => {
              const textarea = document.createElement("textarea");
              textarea.value = url;
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand("copy");
              document.body.removeChild(textarea);
              toast.success(t("toast.linkCopied") || "Link copied!");
            });
          }}
          startIcon={<ShareIcon />}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none" }}
        >
          {t("share.share")}
        </Button>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GalleryViewPopup;
